import { supabase } from '@/integrations/supabase/client';
import type { 
  AudienceModeSession,
  AudienceModeSessionInsert,
  AudienceSessionWithDetails,
  OutletOperationResult 
} from '@/types/mediaOutlets';
import { customOutletReviewService } from './customOutletReviewService';
import { mediaOutletService } from './mediaOutletService';
import { toast } from 'sonner';

export const audienceModeService = {
  async createSession(
    userId: string,
    userType: 'supabase' | 'internal',
    gameId: string,
    locationType: 'onsite' | 'watching_in' | 'listening_in',
    outletId?: string,
    customOutletName?: string
  ): Promise<OutletOperationResult> {
    try {
      // Validate XOR constraint for watching/listening
      if (locationType !== 'onsite') {
        const hasOutlet = !!outletId;
        const hasCustom = !!customOutletName;
        
        if (hasOutlet === hasCustom) {
          throw new Error('Must provide either outlet_id OR custom_outlet_name, not both');
        }
      }
      
      // Check for existing active session
      const { data: existingSession } = await supabase
        .from('audience_mode_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('game_id', gameId)
        .eq('is_active', true)
        .maybeSingle();
      
      if (existingSession) {
        return { 
          success: false, 
          error: 'You already have an active session for this game',
          data: existingSession 
        };
      }
      
      // Handle custom outlet submission
      let customQueueId = null;
      if (customOutletName) {
        const submitResult = await customOutletReviewService.submitCustomOutlet(
          customOutletName,
          userId,
          userType,
          gameId
        );
        
        if (submitResult.success && submitResult.data) {
          customQueueId = submitResult.data.id;
        }
      }
      
      // Create session
      const sessionData: AudienceModeSessionInsert = {
        user_id: userId,
        user_type: userType,
        game_id: gameId,
        location_type: locationType,
        outlet_id: outletId || null,
        custom_outlet_name: customOutletName || null,
        is_custom_entry: !!customOutletName,
        custom_queue_id: customQueueId,
        is_active: true
      };
      
      const { data, error } = await supabase
        .from('audience_mode_sessions')
        .insert(sessionData)
        .select()
        .single();
      
      if (error) throw error;
      
      // Increment outlet usage if outlet selected
      if (outletId) {
        await mediaOutletService.incrementUsageCount(outletId);
      }
      
      console.log('Audience session created successfully:', data);
      
      // Dispatch real-time event
      window.dispatchEvent(new CustomEvent('audience-session-started', { 
        detail: { session: data, gameId }
      }));
      
      return { success: true, data };
    } catch (error: any) {
      console.error('Error creating audience session:', error);
      return { success: false, error: error.message };
    }
  },

  async endSession(sessionId: string): Promise<OutletOperationResult> {
    try {
      // Get session to calculate duration
      const { data: session, error: fetchError } = await supabase
        .from('audience_mode_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();
      
      if (fetchError) throw fetchError;
      
      const endTime = new Date();
      const startTime = new Date(session.session_started_at);
      const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / 60000);
      
      const { data, error } = await supabase
        .from('audience_mode_sessions')
        .update({
          is_active: false,
          session_ended_at: endTime.toISOString()
        })
        .eq('id', sessionId)
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('Audience session ended:', data);
      
      // Dispatch real-time event
      window.dispatchEvent(new CustomEvent('audience-session-ended', { 
        detail: { sessionId, session: data }
      }));
      
      return { success: true, data };
    } catch (error: any) {
      console.error('Error ending audience session:', error);
      return { success: false, error: error.message };
    }
  },

  async getActiveSessionsForGame(gameId: string): Promise<AudienceModeSession[]> {
    try {
      const { data, error } = await supabase
        .from('audience_mode_sessions')
        .select('*')
        .eq('game_id', gameId)
        .eq('is_active', true)
        .order('session_started_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching active sessions for game:', error);
      return [];
    }
  },

  async getUserActiveSession(userId: string, gameId: string): Promise<AudienceModeSession | null> {
    try {
      const { data, error } = await supabase
        .from('audience_mode_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('game_id', gameId)
        .eq('is_active', true)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching user active session:', error);
      return null;
    }
  },

  async getSessionStats(gameId: string): Promise<{
    total: number;
    onsite: number;
    watching: number;
    listening: number;
    byOutlet: Record<string, number>;
  }> {
    try {
      const { data, error } = await supabase
        .from('audience_mode_sessions')
        .select('*, media_outlets!outlet_id(*)')
        .eq('game_id', gameId)
        .eq('is_active', true);
      
      if (error) throw error;
      
      const sessions = data || [];
      const stats = {
        total: sessions.length,
        onsite: sessions.filter(s => s.location_type === 'onsite').length,
        watching: sessions.filter(s => s.location_type === 'watching_in').length,
        listening: sessions.filter(s => s.location_type === 'listening_in').length,
        byOutlet: {} as Record<string, number>
      };
      
      sessions.forEach(session => {
        if (session.outlet_id) {
          const outletName = (session.media_outlets as any)?.outlet_name || 'Unknown';
          stats.byOutlet[outletName] = (stats.byOutlet[outletName] || 0) + 1;
        }
      });
      
      return stats;
    } catch (error) {
      console.error('Error fetching session stats:', error);
      return { total: 0, onsite: 0, watching: 0, listening: 0, byOutlet: {} };
    }
  },

  async getSessionsByOutlet(outletId: string): Promise<AudienceModeSession[]> {
    try {
      const { data, error } = await supabase
        .from('audience_mode_sessions')
        .select('*')
        .eq('outlet_id', outletId)
        .order('session_started_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching sessions by outlet:', error);
      return [];
    }
  },

  async updateSession(
    sessionId: string,
    updates: Partial<AudienceModeSessionInsert>
  ): Promise<OutletOperationResult> {
    try {
      const { data, error } = await supabase
        .from('audience_mode_sessions')
        .update(updates)
        .eq('id', sessionId)
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('Audience session updated:', data);
      return { success: true, data };
    } catch (error: any) {
      console.error('Error updating audience session:', error);
      return { success: false, error: error.message };
    }
  }
};
